import React from 'react';
import { FileText, Calendar, Tag, Zap } from 'lucide-react';

const PlatformChangelog = () => {
  const updates = [
    {
      version: 'v2.4.0',
      date: '2024-01-08',
      type: 'feature',
      title: 'Enhanced Header User Dropdown',
      description: 'Added platform utilities menu with Knowledge Base, Live Agent Support, System Status, Platform Changelog, and Keyboard Shortcuts.',
      features: ['New dropdown menu items', 'Role-based access control', 'Improved navigation']
    },
    {
      version: 'v2.3.5',
      date: '2024-01-05',
      type: 'improvement',
      title: 'SMS Performance Improvements',
      description: 'Optimized SMS processing pipeline for better throughput and reduced latency.',
      features: ['30% faster processing', 'Reduced memory usage', 'Better error handling']
    },
    {
      version: 'v2.3.4',
      date: '2024-01-03',
      type: 'bugfix',
      title: 'Fixed DLR Processing Issue',
      description: 'Resolved an issue where some delivery receipts were not being processed correctly.',
      features: ['Fixed DLR parsing', 'Improved retry logic', 'Better logging']
    },
    {
      version: 'v2.3.3',
      date: '2023-12-28',
      type: 'security',
      title: 'Security Updates',
      description: 'Applied security patches and improved authentication mechanisms.',
      features: ['Updated dependencies', 'Enhanced password validation', 'Improved session management']
    },
    {
      version: 'v2.3.2',
      date: '2023-12-20',
      type: 'feature',
      title: 'Advanced Routing Rules',
      description: 'New routing engine with support for complex routing conditions and failover logic.',
      features: ['Conditional routing', 'Load balancing', 'Automatic failover']
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Zap className="w-4 h-4 text-purple-500" />;
      case 'improvement':
        return <Tag className="w-4 h-4 text-blue-500" />;
      case 'bugfix':
        return <FileText className="w-4 h-4 text-green-500" />;
      case 'security':
        return <Calendar className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'feature':
        return 'bg-purple-100 text-purple-800';
      case 'improvement':
        return 'bg-blue-100 text-blue-800';
      case 'bugfix':
        return 'bg-green-100 text-green-800';
      case 'security':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText className="w-6 h-6" />
          Platform Changelog
        </h1>
        <p className="text-gray-600 mt-1">Stay updated with the latest features and improvements</p>
      </div>

      <div className="space-y-6">
        {updates.map((update, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                {getTypeIcon(update.type)}
                <div>
                  <h3 className="font-semibold text-gray-900">{update.title}</h3>
                  <p className="text-sm text-gray-500">
                    Version {update.version} • {update.date}
                  </p>
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(update.type)}`}>
                {update.type}
              </span>
            </div>
            
            <p className="text-gray-700 mb-4">{update.description}</p>
            
            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">What's new:</h4>
              <ul className="space-y-1">
                {update.features.map((feature, idx) => (
                  <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-indigo-50 p-6 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2">Looking for something specific?</h3>
        <p className="text-sm text-gray-600 mb-4">
          Browse our archive for older updates or search for specific features.
        </p>
        <div className="flex gap-3">
          <button className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
            View Archive
          </button>
          <button className="text-indigo-600 hover:text-indigo-700 px-4 py-2 rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors text-sm">
            Subscribe to Updates
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlatformChangelog;
